import type {
  GetCrmQuotationResponse,
  ListCrmQuotationsResponse,
  QuotationCatalogResponse,
  QuotationDraftPayload,
  UpsertCrmQuotationResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

export function quotationCatalogQueryKey(
  brand: string | null,
): readonly ['quotation-catalog', string | null] {
  return ['quotation-catalog', brand] as const;
}

export async function getQuotationCatalog(
  fetchImpl: typeof fetch,
  brand: string | null,
  signal?: AbortSignal,
): Promise<QuotationCatalogResponse> {
  const query = brand !== null && brand.length > 0 ? `?brand=${encodeURIComponent(brand)}` : '';
  return opsApiRequest<QuotationCatalogResponse>(
    `/v1/ops/quotation/catalog${query}`,
    { signal },
    fetchImpl,
  );
}

export function useQuotationCatalog(
  brand: string | null,
  enabled: boolean,
): UseQueryResult<QuotationCatalogResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: quotationCatalogQueryKey(brand),
    queryFn: ({ signal }) => getQuotationCatalog(fetchImpl, brand, signal),
    enabled,
  });
}

function brandQuery(brand: string | null): string {
  return brand !== null && brand.length > 0 ? `?brand=${encodeURIComponent(brand)}` : '';
}

export function savedQuotationsQueryKey(
  brand: string | null,
): readonly ['quotation-saved', string | null] {
  return ['quotation-saved', brand] as const;
}

export async function listSavedQuotations(
  fetchImpl: typeof fetch,
  brand: string | null,
  signal?: AbortSignal,
): Promise<ListCrmQuotationsResponse> {
  return opsApiRequest<ListCrmQuotationsResponse>(
    `/v1/ops/quotation/quotations${brandQuery(brand)}`,
    { signal },
    fetchImpl,
  );
}

export async function getSavedQuotation(
  fetchImpl: typeof fetch,
  id: string,
  brand: string | null,
  signal?: AbortSignal,
): Promise<GetCrmQuotationResponse> {
  return opsApiRequest<GetCrmQuotationResponse>(
    `/v1/ops/quotation/quotations/${encodeURIComponent(id)}${brandQuery(brand)}`,
    { signal },
    fetchImpl,
  );
}

export async function upsertSavedQuotation(
  fetchImpl: typeof fetch,
  brand: string | null,
  draft: QuotationDraftPayload,
): Promise<UpsertCrmQuotationResponse> {
  return opsApiRequest<UpsertCrmQuotationResponse>(
    `/v1/ops/quotation/quotations${brandQuery(brand)}`,
    { method: 'PUT', body: { draft } },
    fetchImpl,
  );
}

export function useSavedQuotations(
  brand: string | null,
  enabled: boolean,
): UseQueryResult<ListCrmQuotationsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: savedQuotationsQueryKey(brand),
    queryFn: ({ signal }) => listSavedQuotations(fetchImpl, brand, signal),
    enabled,
  });
}

export function useUpsertSavedQuotation(): UseMutationResult<
  UpsertCrmQuotationResponse,
  Error,
  { readonly brand: string | null; readonly draft: QuotationDraftPayload }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ brand, draft }) => upsertSavedQuotation(fetchImpl, brand, draft),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: savedQuotationsQueryKey(variables.brand) });
    },
  });
}
